import { Business as OfficeIcon } from '@mui/icons-material'
import { Card, CardContent, Typography, Box } from '@mui/material'
import { useNavigate } from 'react-router-dom'

import { OfficeWithRole } from '../../utils/profileHelpers'
import { RoleDisplay } from '../common'

/**
 * Props for the OfficeCard component
 */
export interface OfficeCardProps {
  /** Office data with role information */
  office: OfficeWithRole
}

/**
 * OfficeCard - Individual office display component
 *
 * Displays individual office information with role badge.
 * Shows office name and user's role in that office.
 * Includes role descriptions for each role type via the RoleBadge tooltip.
 * Clickable to navigate to office detail view.
 */
const OfficeCard = ({ office }: OfficeCardProps) => {
  const navigate = useNavigate()

  const handleCardClick = () => {
    if (office.oid) {
      navigate(`/office/${office.oid}`)
    }
  }

  return (
    <Card
      variant='outlined'
      sx={{
        mb: 2,
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 2,
          transform: 'translateY(-1px)'
        }
      }}
      onClick={handleCardClick}
    >
      <CardContent>
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          flexWrap='wrap'
          gap={2}
        >
          {/* Office name with icon */}
          <Box display='flex' alignItems='center' gap={1} flex={1} minWidth={0}>
            <OfficeIcon color='action' fontSize='small' />
            <Typography
              variant='h6'
              component='h3'
              sx={{
                fontWeight: 'medium',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {office.name || 'Unnamed Office'}
            </Typography>
          </Box>

          {/* Role display */}
          <Box flexShrink={0}>
            <RoleDisplay role={office.userRole} variant='chip' size='small' />
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default OfficeCard
